import { BellOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import PropTypes from "prop-types";

const INTERVIEW_TYPE_LABELS = {
  InitialInterview: "Initial Interview",
  TechnicalInterview: "Technical Interview",
  FinalInterview: "Final Interview",
  PanelInterview: "Panel Interview",
  HRInterview: "HR Interview",
};

const InterviewHoverContent = ({ interview }) => {
  if (!interview) return null;

  const { type, scheduledDate, startTime, endTime, applicantName, jobTitle, onlineLink, onSiteLocation } = interview;

  if (!type || !scheduledDate || !startTime || !endTime) return null;

  const displayType = INTERVIEW_TYPE_LABELS[type] || type;

  const getDynamicReminder = (targetDate) => {
    const today = dayjs().startOf("day");
    const interviewDate = dayjs(targetDate).startOf("day");
    const diffDays = interviewDate.diff(today, "day");

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays > 1) return `${diffDays} days before`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    return "";
  };

  return (
    <div className="flex items-start p-4 gap-3">
      <div className="w-3 h-3 bg-[#FF9D4D] rounded-sm mt-2" />
      <div className="w-72 font-sans relative">
        <h3 className="font-semibold text-xl m-0 p-0 text-gray-800 tracking-tight">{displayType}</h3>

        <p className="text-gray-700 text-base m-0 p-0 font-normal">
          {dayjs(scheduledDate).format("MMMM DD")} ({dayjs(`2000-01-01 ${startTime}`).format("h:mm A")} - {dayjs(`2000-01-01 ${endTime}`).format("h:mm A")})
        </p>

        <div className="w-full h-[2px] bg-[#FF9D4D] mb-5 mt-2" />

        <div className="space-y-1">
          <div>
            <span className="text-gray-800 text-sm">Applicant: </span>
            <span className="text-gray-700 text-sm">{applicantName}</span>
          </div>

          <div>
            <span className="text-gray-800 text-sm">Applied Position: </span>
            <span className="text-gray-700 text-sm">{jobTitle}</span>
          </div>

          {onSiteLocation ? (
            <div className="flex flex-col">
              <span className="text-gray-800 text-sm">Location:</span>
              <span className="text-gray-700 text-sm leading-relaxed">{onSiteLocation}</span>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-gray-800 text-sm mb-1">Interview URL:</span>
              <a href={onlineLink} target="_blank" rel="noreferrer" className="text-blue-500 text-xs break-all underline hover:text-blue-700">
                {onlineLink}
              </a>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3 text-gray-800 text-sm">
          <BellOutlined className="text-lg" />
          <span>{getDynamicReminder(scheduledDate)}</span>
        </div>
      </div>
    </div>
  );
};

InterviewHoverContent.propTypes = {
  interview: PropTypes.shape({
    type: PropTypes.string,
    scheduledDate: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    applicantName: PropTypes.string,
    jobTitle: PropTypes.string,
    onlineLink: PropTypes.string,
    onSiteLocation: PropTypes.string,
  }),
};

export default observer(InterviewHoverContent);
